import { reportError } from "$lib/errorHandling/errorReporter";
import { logger } from "$lib/utils/logger";

interface ParsedUrl {
  displayId: string;
  devOverlay: boolean;
  remoteLog: boolean | undefined;
}

const DISPLAYID_VALIDATION_PATTERN = /^[A-Za-z0-9_-]+$/; // UUID or nanoid: alphanumeric, hyphen, underscore

/**
 * Parses elev8 display URLs.
 *
 * Query params (precedence varies per param by design):
 *   ?displayId=<id>                  Alternative to /display/:id path
 *   ?devOverlay=true|false           Toggle dev overlay; defaults to true in dev mode.
 *                                    Overridden ON by config.settings.debugging_enabled
 *                                    via $effect in ManualSpotController.
 *   ?logLevel=DEBUG|INFO|WARN|ERROR  Set logger level. No config equivalent today.
 *   ?remoteLog=true|false            Overrides config.settings.remote_logging_enabled
 *                                    via `??` in App.svelte (URL wins; explicit false
 *                                    force-disables). Overridden ON by
 *                                    config.settings.debugging_enabled via `||` in the
 *                                    same gate.
 *   ?preview=true                    Marks this load as a preview render. Parsed and
 *                                    consumed via $lib/utils/previewMode, not here.
 *
 * config.settings.debugging_enabled is a force-on "debug mode" macro spanning devOverlay
 * and remoteLog. It exists because central currently exposes only this single staff-toggle
 * field — transitional pending explicit per-flag controls (issues #223, #225). Log level
 * is not part of the macro; set ?logLevel=DEBUG in the URL when verbose logs are needed.
 */
function parseElev8Url(urlString: string): ParsedUrl {
  let devOverlay = import.meta.env.MODE === "development"; // default to true in dev mode
  let remoteLog: boolean | undefined;
  let url = new URL(urlString);
  url = removeEntrypointFromUrlAndHistory(url);
  let displayId = validateDisplayId(getDisplayIdFromUrl(url));

  if (url.searchParams.has("devOverlay")) {
    devOverlay = url.searchParams.get("devOverlay") === "true";
    logger.warn(
      `[URL] devOverlay is set by url search param to << ${devOverlay} >>. This should only be used for development purposes.`
    );
  }

  if (url.searchParams.has("remoteLog")) {
    remoteLog = url.searchParams.get("remoteLog") === "true";
    logger.warn(
      `[URL] remoteLog is set by url search param << ${remoteLog} >>.`
    );
  }

  if (url.searchParams.has("logLevel")) {
    const logLevel = url.searchParams.get("logLevel") ?? "info";
    const setLevel = logger.setLevel(logLevel);
    logger.warn(`[URL] logLevel is set by url search param << ${setLevel} >>.`);
  }

  return { displayId, devOverlay, remoteLog };
}

function getDisplayIdFromUrl(url: URL): string {
  if (url.searchParams.has("displayId")) {
    return url.searchParams.get("displayId") ?? "";
  }
  if (url.pathname.startsWith("/display/")) {
    const parts = url.pathname.split("/");
    const displayIdIndex = parts.indexOf("display") + 1;
    return parts[displayIdIndex] ?? "";
  }
  logger.info(
    `[URL] Invalid URL (no attempt to set displayId): ${url.pathname}`
  );
  return "";
}

// returns validated displayId or empty string if invalid
function validateDisplayId(displayId: string): string {
  if (!displayId) return "";
  if (DISPLAYID_VALIDATION_PATTERN.test(displayId)) {
    logger.info(`[URL] found displayId: ${displayId}`);
    return displayId;
  }
  reportError(
    `[URL] The extracted displayId << ${displayId} >> does not match the expected format.`,
    new TypeError("Invalid displayId format")
  );
  return "";
}

function removeEntrypointFromUrlAndHistory(url: URL): URL {
  const pathname = url.pathname;
  const replaceString = "/entrypoint/display/";

  if (pathname.startsWith(replaceString)) {
    url.pathname = url.pathname.replace(replaceString, "/display/");
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    logger.debug(
      `[URL] Rewrote url path from ${pathname} to ${url.pathname} to remove /entrypoint prefix`
    );
  }
  return url;
}

// For debugging: extract domain and filename from url for logging purposes (e.g. "example.com//video.mp4")
function domainAndFileFromUrl(
  assetUrl: string,
  truncateFilename = false
): string {
  if (!assetUrl || typeof assetUrl !== "string") return "";
  const sub = assetUrl.split("/");
  const domain = sub[2] ?? "";
  let file = sub[sub.length - 1] ?? "";
  if (truncateFilename && file.length > 16) {
    const _max = 16;
    file = file.slice(0, _max / 2) + "..." + file.slice(file.length - _max);
  }
  return `${domain}//${file}`;
}

export { parseElev8Url, domainAndFileFromUrl };
export type { ParsedUrl };
